import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios, { API } from '../utils/api';
import { useCommunityAuth } from '../context/CommunityAuthContext';
import CommunityTopHeader from '../components/community/CommunityTopHeader';
import { Calendar, MapPin, Link2, Video, User, Wand2, Sparkles, ImagePlus, RefreshCw, X, Upload } from 'lucide-react';

const TEAL = '#4DB6AC';
const COMM = `${API}/community`;

export default function CommunityCreateEventPage() {
  const navigate = useNavigate();
  const { token } = useCommunityAuth();
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const [form, setForm] = useState({
    title: '', description: '', location: '',
    start_date: '', end_date: '', verification_method: 'manual',
    reward_stars: 0, is_realtime: false, is_featured: false, frequency: 'once',
    eventMode: 'inperson', meetLink: '',
    pocName: '', pocEmail: '',
    geo_tag_required: false, photo_tag: '',
    // New fields
    challenge_type: 'Fun & Sport',     // Fun & Sport | Learning & Growth | Creativity | Entertainment
    schedule_mode: 'single',           // 'single' | 'multi'
    start_time: '', end_time: '',
    weekly_day: 1,                     // 0=Sun..6=Sat (default Mon)
    monthly_day: 1,                    // 1..31
    recurrence_end_date: '',
    min_participants: '', max_participants: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [rephrasing, setRephrasing] = useState(false);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [aiImagePreview, setAiImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  // Tracks whether the active image came from upload or AI gen — used purely for
  // the preview label. The submission path is identical for both (multipart upload).
  const [imageSource, setImageSource] = useState(null); // 'upload' | 'ai' | null
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef(null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const MAX_IMAGE_BYTES = 5 * 1024 * 1024; // 5MB
  const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png'];

  const handlePickUpload = () => {
    setUploadError('');
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleFileSelected = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setUploadError('Only JPG and PNG images are supported.');
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      setUploadError('Image is too large. Max size is 5MB.');
      return;
    }
    // Replace whatever was there (AI gen or previous upload). `imageFile`
    // is set synchronously right after validation so a fast submit can
    // never race with the FileReader — matches CommunityCreatePostPage.
    // The FileReader below only feeds the local preview.
    setImageFile(file);
    setImageSource('upload');
    setUploadError('');
    const reader = new FileReader();
    reader.onload = () => {
      setAiImagePreview(reader.result);  // reuse the same preview state
    };
    reader.readAsDataURL(file);
  };

  const clearImage = () => {
    setAiImagePreview(null);
    setImageFile(null);
    setImageSource(null);
    setUploadError('');
  };

  const handleRephrase = async () => {
    if (!form.description.trim() || rephrasing) return;
    setRephrasing(true);
    try {
      const res = await axios.post(`${COMM}/rephrase-text`, { text: form.description }, authHeaders);
      set('description', res.data.rephrased_text);
    } catch (err) { alert(err.response?.data?.detail || 'Failed to rephrase'); }
    finally { setRephrasing(false); }
  };

  const handleGenerateImage = async () => {
    if (!form.title.trim() && !form.description.trim()) { alert('Add a title or description first'); return; }
    setGeneratingImage(true); setAiImagePreview(null);
    setUploadError('');
    try {
      const res = await axios.post(`${COMM}/generate-image`, { post_text: `${form.title}. ${form.description}` }, authHeaders);
      const dataUrl = `data:${res.data.mime_type || 'image/png'};base64,${res.data.image_data}`;
      setAiImagePreview(dataUrl);
      // Convert to file
      const [header, b64] = dataUrl.split(',');
      const mime = header.match(/:(.*?);/)?.[1] || 'image/png';
      const bytes = atob(b64);
      const arr = new Uint8Array(bytes.length);
      for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
      setImageFile(new File([arr], 'ai-event-image.png', { type: mime }));
      setImageSource('ai');
    } catch (err) { alert(err.response?.data?.detail || 'Failed to generate image'); }
    finally { setGeneratingImage(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setSubmitting(true);
    if (form.eventMode === 'online' && form.meetLink.trim()) {
      try { new URL(form.meetLink.trim()); } catch {
        alert('Please enter a valid meeting link URL');
        setSubmitting(false); return;
      }
    }
    try {
      let image_url = null;
      if (imageFile) {
        const formData = new FormData();
        formData.append('file', imageFile);
        const uploadRes = await axios.post(`${COMM}/upload`, formData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
        image_url = uploadRes.data.url;
      }
      // Single-day events: start_date and end_date are the same date.
      const isSingle = form.schedule_mode === 'single';
      const startDate = form.start_date;
      const endDate = isSingle ? form.start_date : (form.end_date || form.start_date);
      const payload = {
        title: form.title, description: form.description,
        event_type: 'event',
        location: form.eventMode === 'inperson' ? form.location : null,
        meetLink: form.eventMode === 'online' ? form.meetLink.trim() : null,
        eventMode: form.eventMode,
        event_date: startDate, start_date: startDate,
        end_date: endDate,
        verification_method: form.verification_method,
        reward_stars: parseInt(form.reward_stars) || 0,
        is_realtime: form.is_realtime,
        is_featured: !!form.is_featured,
        frequency: form.frequency,
        pocName: form.pocName.trim() || null,
        pocEmail: form.pocEmail.trim() || null,
        image_url: image_url,
        geo_tag_required: form.verification_method === 'geotag' || form.verification_method === 'both',
        photo_tag: (form.verification_method === 'photo' || form.verification_method === 'both') ? 'completion_photo' : '',
        // New fields
        challenge_type: form.challenge_type,
        schedule_mode: form.schedule_mode,
        start_time: isSingle ? (form.start_time || null) : null,
        end_time: isSingle ? (form.end_time || null) : null,
        weekly_day: form.frequency === 'weekly' ? parseInt(form.weekly_day) : null,
        monthly_day: form.frequency === 'monthly' ? parseInt(form.monthly_day) : null,
        recurrence_end_date: (form.frequency === 'weekly' || form.frequency === 'monthly') ? (form.recurrence_end_date || null) : null,
        min_participants: form.min_participants !== '' ? parseInt(form.min_participants) : null,
        max_participants: form.max_participants !== '' ? parseInt(form.max_participants) : null,
      };
      await axios.post(`${COMM}/events`, payload, authHeaders);
      navigate('/community');
    } catch (err) { alert(err.response?.data?.detail || 'Failed to create event'); }
    finally { setSubmitting(false); }
  };

  const labelStyle = { display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 };
  const inputStyle = { width: '100%', padding: '12px 14px', borderRadius: 10, border: '1.5px solid #E5E7EB', fontSize: 14, outline: 'none', boxSizing: 'border-box', background: '#FAFAFA' };
  const selectStyle = { ...inputStyle, appearance: 'none', backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L6 6L11 1' stroke='%236B7280' stroke-width='2' stroke-linecap='round'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 14px center' };

  return (
    <div style={{ minHeight: '100vh', background: '#F5F7FA' }}>
      <CommunityTopHeader showBack />
      <div style={{ width: '100%', maxWidth: 380, margin: '0 auto', padding: 16 }}>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div>
            <label style={labelStyle}>Title *</label>
            <input style={inputStyle} value={form.title} onChange={e => set('title', e.target.value)} required data-testid="input-title" placeholder="Event name" />
          </div>
          <div>
            <label style={labelStyle}>Description *</label>
            <div style={{ position: 'relative' }}>
              <textarea style={{ ...inputStyle, resize: 'vertical', paddingRight: 44 }} value={form.description} onChange={e => set('description', e.target.value)} rows={3} required data-testid="input-description" placeholder="What's this event about?" />
              <button type="button" data-testid="ai-rephrase-btn" onClick={handleRephrase} disabled={rephrasing || !form.description.trim()}
                title="AI Rephrase"
                style={{
                  position: 'absolute', top: 8, right: 8, width: 32, height: 32,
                  borderRadius: 8, border: 'none', cursor: rephrasing ? 'not-allowed' : 'pointer',
                  background: '#F0FDF4', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  opacity: !form.description.trim() ? 0.4 : 1,
                }}>
                {rephrasing ? <RefreshCw size={15} color={TEAL} style={{ animation: 'spin 1s linear infinite' }} /> : <Wand2 size={15} color={TEAL} />}
              </button>
            </div>
          </div>
          {/* Event Image — Upload or AI Generate (only one active at a time) */}
          <div>
            <label style={labelStyle}><ImagePlus size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />Event Image</label>

            {/* Hidden native file input — triggered by the Upload button */}
            <input ref={fileInputRef} type="file" accept="image/jpeg,image/png"
              data-testid="event-image-file-input"
              onChange={handleFileSelected} style={{ display: 'none' }} />

            {aiImagePreview ? (
              <div data-testid="event-image-preview" style={{ position: 'relative', borderRadius: 12, overflow: 'hidden', marginBottom: 10 }}>
                <img src={aiImagePreview} alt="Event preview" style={{ width: '100%', height: 160, objectFit: 'cover', borderRadius: 12 }} />
                <button type="button" data-testid="event-image-remove" onClick={clearImage}
                  style={{ position: 'absolute', top: 8, right: 8, width: 28, height: 28, borderRadius: '50%', background: 'rgba(0,0,0,0.6)', border: 'none', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                  <X size={14} />
                </button>
                <span style={{
                  position: 'absolute', bottom: 8, left: 8,
                  background: 'rgba(0,0,0,0.6)', color: '#fff',
                  fontSize: 11, fontWeight: 600, padding: '4px 8px', borderRadius: 6,
                  letterSpacing: 0.5, textTransform: 'uppercase',
                }}>
                  {imageSource === 'ai' ? 'AI generated' : 'Uploaded'}
                </span>
              </div>
            ) : null}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <button type="button" data-testid="event-image-upload-btn" onClick={handlePickUpload}
                disabled={generatingImage}
                style={{
                  width: '100%', padding: '12px 14px', borderRadius: 10,
                  border: `1.5px ${imageSource === 'upload' ? 'solid' : 'dashed'} ${TEAL}`,
                  background: imageSource === 'upload' ? TEAL : '#f0faf9',
                  color: imageSource === 'upload' ? '#fff' : TEAL,
                  fontSize: 13, fontWeight: 600,
                  cursor: generatingImage ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  opacity: generatingImage ? 0.5 : 1,
                  transition: 'all 0.15s',
                }}>
                <Upload size={15} /> {aiImagePreview && imageSource === 'upload' ? 'Replace upload' : 'Upload'}
              </button>
              <button type="button" data-testid="ai-image-btn" onClick={handleGenerateImage} disabled={generatingImage}
                style={{
                  width: '100%', padding: '12px 14px', borderRadius: 10,
                  border: `1.5px ${imageSource === 'ai' ? 'solid' : 'dashed'} ${TEAL}`,
                  background: imageSource === 'ai' ? TEAL : '#f0faf9',
                  color: imageSource === 'ai' ? '#fff' : TEAL,
                  fontSize: 13, fontWeight: 600,
                  cursor: generatingImage ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  opacity: generatingImage ? 0.6 : 1,
                  transition: 'all 0.15s',
                }}>
                {generatingImage
                  ? <><RefreshCw size={15} style={{ animation: 'spin 1s linear infinite' }} /> Generating…</>
                  : <><Sparkles size={15} /> {aiImagePreview && imageSource === 'ai' ? 'Regenerate' : 'Generate with AI'}</>}
              </button>
            </div>

            {uploadError && (
              <div data-testid="event-image-error" style={{ marginTop: 8, fontSize: 12, color: '#B91C1C' }}>{uploadError}</div>
            )}
            <div style={{ marginTop: 6, fontSize: 11, color: '#9CA3AF' }}>JPG or PNG, up to 5MB. Only one image can be active.</div>
          </div>
          <div>
            <label style={labelStyle}>Challenge Type</label>
            <select style={selectStyle} value={form.challenge_type} onChange={e => set('challenge_type', e.target.value)} data-testid="input-challenge-type">
              <option value="Fun & Sport">Fun &amp; Sport</option>
              <option value="Learning & Growth">Learning &amp; Growth</option>
              <option value="Creativity">Creativity</option>
              <option value="Entertainment">Entertainment</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Frequency</label>
            <select style={selectStyle} value={form.frequency} onChange={e => set('frequency', e.target.value)} data-testid="input-frequency">
              <option value="once">Once</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
            {form.frequency === 'weekly' && (
              <div style={{ marginTop: 10 }}>
                <label style={labelStyle}>Day of week</label>
                <select style={selectStyle} value={form.weekly_day} onChange={e => set('weekly_day', e.target.value)} data-testid="input-weekly-day">
                  <option value={1}>Monday</option>
                  <option value={2}>Tuesday</option>
                  <option value={3}>Wednesday</option>
                  <option value={4}>Thursday</option>
                  <option value={5}>Friday</option>
                  <option value={6}>Saturday</option>
                  <option value={0}>Sunday</option>
                </select>
              </div>
            )}
            {form.frequency === 'monthly' && (
              <div style={{ marginTop: 10 }}>
                <label style={labelStyle}>Day of month</label>
                <select style={selectStyle} value={form.monthly_day} onChange={e => set('monthly_day', e.target.value)} data-testid="input-monthly-day">
                  {Array.from({ length: 31 }, (_, i) => i + 1).map(d => {
                    const suffix = (d === 1 || d === 21 || d === 31) ? 'st' : (d === 2 || d === 22) ? 'nd' : (d === 3 || d === 23) ? 'rd' : 'th';
                    return <option key={d} value={d}>{d}{suffix}</option>;
                  })}
                </select>
              </div>
            )}
            {(form.frequency === 'weekly' || form.frequency === 'monthly') && (
              <div style={{ marginTop: 10 }}>
                <label style={labelStyle}><Calendar size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />End Recurrence Date</label>
                <input type="date" style={inputStyle} value={form.recurrence_end_date} onChange={e => set('recurrence_end_date', e.target.value)} data-testid="input-recurrence-end-date" />
              </div>
            )}
          </div>
          {/* Event Mode Toggle */}
          <div>
            <label style={labelStyle}>Event Mode</label>
            <div data-testid="event-mode-toggle" style={{ display: 'flex', borderRadius: 10, border: '1.5px solid #E5E7EB', overflow: 'hidden' }}>
              {[{ id: 'inperson', label: 'In-Person', icon: <MapPin size={14} /> }, { id: 'online', label: 'Online', icon: <Video size={14} /> }].map(opt => (
                <button key={opt.id} type="button" data-testid={`mode-${opt.id}`}
                  onClick={() => set('eventMode', opt.id)}
                  style={{
                    flex: 1, padding: '10px 0', border: 'none', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    fontSize: 13, fontWeight: 600, transition: 'all 0.15s',
                    background: form.eventMode === opt.id ? TEAL : '#FAFAFA',
                    color: form.eventMode === opt.id ? '#fff' : '#6B7280',
                  }}>
                  {opt.icon} {opt.label}
                </button>
              ))}
            </div>
          </div>
          {form.eventMode === 'inperson' ? (
            <div>
              <label style={labelStyle}><MapPin size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />Location</label>
              <input style={inputStyle} value={form.location} onChange={e => set('location', e.target.value)} data-testid="input-location" placeholder="Where is it?" />
            </div>
          ) : (
            <div>
              <label style={labelStyle}><Link2 size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />Meeting Link</label>
              <input style={inputStyle} value={form.meetLink} onChange={e => set('meetLink', e.target.value)} data-testid="input-meetlink" placeholder="https://meet.google.com/..." />
            </div>
          )}

          {/* Participants */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>Min Participants</label>
              <input type="number" min="0" style={inputStyle} value={form.min_participants}
                onChange={e => set('min_participants', e.target.value)} data-testid="input-min-participants" placeholder="0" />
            </div>
            <div>
              <label style={labelStyle}>Max Participants</label>
              <input type="number" min="0" style={inputStyle} value={form.max_participants}
                onChange={e => set('max_participants', e.target.value)} data-testid="input-max-participants" placeholder="No limit" />
            </div>
          </div>

          {/* Date & Time — Single Day vs Multi-Day */}
          <div>
            <label style={labelStyle}>Date &amp; Time</label>
            <div data-testid="schedule-mode-toggle" style={{ display: 'flex', borderRadius: 10, border: '1.5px solid #E5E7EB', overflow: 'hidden', marginBottom: 10 }}>
              {[{ id: 'single', label: 'Single Day' }, { id: 'multi', label: 'Multi-Day' }].map(opt => (
                <button key={opt.id} type="button" data-testid={`schedule-${opt.id}`}
                  onClick={() => set('schedule_mode', opt.id)}
                  style={{
                    flex: 1, padding: '10px 0', border: 'none', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    fontSize: 13, fontWeight: 600, transition: 'all 0.15s',
                    background: form.schedule_mode === opt.id ? TEAL : '#FAFAFA',
                    color: form.schedule_mode === opt.id ? '#fff' : '#6B7280',
                  }}>
                  {opt.label}
                </button>
              ))}
            </div>
            {form.schedule_mode === 'single' ? (
              <>
                <div style={{ marginBottom: 10 }}>
                  <label style={labelStyle}><Calendar size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />Date *</label>
                  <input type="date" style={inputStyle} value={form.start_date} onChange={e => set('start_date', e.target.value)} required data-testid="input-start-date" />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={labelStyle}>Start Time</label>
                    <input type="time" style={inputStyle} value={form.start_time} onChange={e => set('start_time', e.target.value)} data-testid="input-start-time" />
                  </div>
                  <div>
                    <label style={labelStyle}>End Time</label>
                    <input type="time" style={inputStyle} value={form.end_time} onChange={e => set('end_time', e.target.value)} data-testid="input-end-time" />
                  </div>
                </div>
              </>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle}><Calendar size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />Start Date *</label>
                  <input type="date" style={inputStyle} value={form.start_date} onChange={e => set('start_date', e.target.value)} required data-testid="input-start-date" />
                </div>
                <div>
                  <label style={labelStyle}><Calendar size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />End Date *</label>
                  <input type="date" style={inputStyle} value={form.end_date} onChange={e => set('end_date', e.target.value)} required={form.schedule_mode === 'multi'} data-testid="input-end-date" />
                </div>
              </div>
            )}
          </div>

          {/* Verification Method */}
          <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 12, padding: 16 }}>
            <label style={{ ...labelStyle, color: '#166534', marginBottom: 10 }}>Completion Verification Method</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {[
                { value: 'photo', label: 'Photo Upload', desc: 'User uploads a completion photo' },
                { value: 'geotag', label: 'Geo-tag', desc: 'User shares live location' },
                { value: 'both', label: 'Photo + Geo-tag', desc: 'Both required' },
                { value: 'manual', label: 'Manual (Admin marks)', desc: 'No user submission needed' },
              ].map(opt => (
                <label key={opt.value} data-testid={`vm-${opt.value}`}
                  style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px', borderRadius: 8, cursor: 'pointer', background: form.verification_method === opt.value ? '#DCFCE7' : '#fff', border: `1.5px solid ${form.verification_method === opt.value ? '#22C55E' : '#E5E7EB'}`, transition: 'all 0.15s' }}>
                  <input type="radio" name="vm" value={opt.value} checked={form.verification_method === opt.value}
                    onChange={e => set('verification_method', e.target.value)}
                    style={{ marginTop: 2, accentColor: '#22C55E' }} />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#1F2937' }}>{opt.label}</div>
                    <div style={{ fontSize: 12, color: '#6B7280' }}>{opt.desc}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
          <div>
            <label style={labelStyle}>Reward Stars</label>
            <input type="number" min="0" max="1000" style={inputStyle} value={form.reward_stars}
              onChange={e => set('reward_stars', e.target.value)} data-testid="input-stars" placeholder="Stars awarded on approval" />
            <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 4 }}>Stars will only be credited after admin approves the submission</div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={form.is_realtime} onChange={e => set('is_realtime', e.target.checked)}
              style={{ accentColor: TEAL }} data-testid="input-realtime" />
            <span style={{ fontSize: 13, color: '#374151' }}>Real-time event (allow submissions from start date)</span>
          </label>

          {/* Pin to Featured — surfaces this event in the community WE featured carousel */}
          <label style={{
            display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
            padding: '12px 14px', borderRadius: 10,
            background: form.is_featured ? '#FFF8E1' : '#F9FAFB',
            border: `1.5px solid ${form.is_featured ? '#F59E0B' : '#E5E7EB'}`,
            transition: 'all 0.15s',
          }}>
            <input type="checkbox" checked={form.is_featured} onChange={e => set('is_featured', e.target.checked)}
              data-testid="input-is-featured"
              style={{ accentColor: '#F59E0B', width: 16, height: 16 }} />
            <span style={{ fontSize: 13, color: '#1F2937', fontWeight: 600 }}>{'\u2B50'} Pin to Featured</span>
            <span style={{ fontSize: 11, color: '#6B7280', marginLeft: 'auto' }}>Shows in the WE featured carousel</span>
          </label>

          {/* Point of Contact */}
          <div>
            <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 6 }}><User size={14} />Point of Contact</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <input style={inputStyle} value={form.pocName} onChange={e => set('pocName', e.target.value)} data-testid="input-poc-name" placeholder="Name" />
              <input type="email" style={inputStyle} value={form.pocEmail} onChange={e => set('pocEmail', e.target.value)} data-testid="input-poc-email" placeholder="Email" />
            </div>
          </div>

          <button type="submit" disabled={submitting} data-testid="submit-event-btn"
            style={{ width: '100%', padding: '14px', background: submitting ? '#9CA3AF' : TEAL, color: '#fff', border: 'none', borderRadius: 10, fontSize: 16, fontWeight: 600, cursor: submitting ? 'not-allowed' : 'pointer', boxShadow: '0 4px 12px rgba(77,182,172,0.25)' }}>
            {submitting ? 'Creating...' : 'Create Event'}
          </button>
        </form>
      </div>
    </div>
  );
}
